import { formatLearnedValue, formatNumber, labelOf } from '@wardrobe/shared';
import { useState, type ReactNode } from 'react';
import { ApiError } from './api.js';

/**
 * Bildschirmrahmen mit iOS-Manier: grosser Titel, der beim Scrollen unter einer
 * Milchglasleiste verschwindet.
 *
 * `min-h-dvh` statt `min-h-screen`: `vh` misst in iOS Safari die Hoehe des
 * ausgeklappten Browsers. Die untere Leiste sass damit beim ersten Aufbau zu tief
 * und sprang erst beim Scrollen an ihren Platz.
 */
export function Screen({ title, subtitle, action, children }: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="min-h-dvh pb-32">
      <header className="safe-top glass sticky top-0 z-20 px-5 pb-3">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h1 className="title-lg truncate">{title}</h1>
            {subtitle !== undefined && (
              <p className="mt-0.5 text-[13px] text-white/45">{subtitle}</p>
            )}
          </div>
          {action}
        </div>
      </header>
      <main className="rise px-5">{children}</main>
    </div>
  );
}

export function Card({ children, className = '', onClick }: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}): ReactNode {
  const base = `card rounded-[var(--radius-card)] p-4 ${className}`;
  if (onClick === undefined) return <div className={base}>{children}</div>;
  return (
    <button type="button" onClick={onClick} className={`press w-full text-left ${base}`}>
      {children}
    </button>
  );
}

export function Button({
  children, onClick, variant = 'primary', disabled, type = 'button', className = '',
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'ghost' | 'danger' | 'plain';
  disabled?: boolean;
  type?: 'button' | 'submit';
  className?: string;
}): ReactNode {
  const styles = {
    // Der Farbverlauf ist minimal — er soll die Flaeche woelben, nicht leuchten.
    primary:
      'bg-gradient-to-b from-[#3a9bff] to-[var(--color-accent)] text-white shadow-[0_6px_20px_-8px_rgba(10,132,255,0.9)] disabled:from-white/10 disabled:to-white/10 disabled:text-white/35 disabled:shadow-none',
    ghost: 'bg-white/8 text-white/90 disabled:text-white/30',
    danger: 'bg-[#ff453a]/90 text-white',
    plain: 'text-[var(--color-accent)]',
  }[variant];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`press rounded-[var(--radius-control)] px-4 py-3 text-[15px] font-semibold ${styles} ${className}`}
    >
      {children}
    </button>
  );
}

/** Segmentierte Auswahl wie in iOS — der aktive Bereich gleitet nicht, er leuchtet. */
export function Segmented({ options, value, onChange }: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}): ReactNode {
  return (
    <div className="-mx-5 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <div className="flex gap-2 pb-1">
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              onClick={() => onChange(o.value)}
              aria-pressed={active}
              className={`press shrink-0 rounded-full px-4 py-2 text-[14px] font-medium ${
                active
                  ? 'bg-white text-black'
                  : 'bg-white/8 text-white/70'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * §11: Bei fehlender Verbindung ein klarer Offline-Zustand — keine Fake-Erfolgsmeldung
 * und keine stille Warteschlange.
 */
export function ErrorBox({ error }: { error: unknown }): ReactNode {
  if (error === null || error === undefined) return null;
  const offline = error instanceof ApiError && error.code === 'OFFLINE';
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : JSON.stringify(error);
  return (
    <div className="rounded-[var(--radius-card)] bg-[#ff453a]/12 p-4 text-[14px] text-[#ff9f9a] ring-1 ring-[#ff453a]/25">
      <p className="font-semibold text-[#ffb4b0]">
        {offline ? 'Keine Verbindung' : 'Das hat nicht geklappt'}
      </p>
      <p className="mt-1 text-[#ff9f9a]/85">{message}</p>
      {offline && (
        <p className="mt-2 text-[#ff9f9a]/60">
          Wardrobe speichert offline nichts zwischen — sobald die Verbindung wieder steht,
          einfach erneut versuchen.
        </p>
      )}
    </div>
  );
}

export function Loading({ label = 'Lädt …' }: { label?: string }): ReactNode {
  return (
    <div className="flex items-center justify-center gap-3 py-10 text-[14px] text-white/45">
      <span className="size-4 animate-spin rounded-full border-2 border-white/20 border-t-white/70" />
      {label}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }): ReactNode {
  return (
    <p className="mx-auto max-w-xs py-12 text-center text-[14px] leading-relaxed text-white/40">
      {children}
    </p>
  );
}

const STATE_COLOR: Record<string, string> = {
  clean: 'bg-[#30d158]/15 text-[#5ee27f]',
  worn_ok: 'bg-[#0a84ff]/15 text-[#6cb8ff]',
  soiled: 'bg-[#ff9f0a]/15 text-[#ffc266]',
  in_hamper: 'bg-[#ff9f0a]/15 text-[#ffc266]',
  washing: 'bg-[#5e5ce6]/18 text-[#a5a3ff]',
  drying: 'bg-[#5e5ce6]/18 text-[#a5a3ff]',
  ironing: 'bg-[#5e5ce6]/18 text-[#a5a3ff]',
  at_care: 'bg-[#bf5af2]/15 text-[#d9a2f7]',
  retired: 'bg-white/8 text-white/40',
};

/**
 * Formularbausteine. Sie stehen hier, seit zwei Bildschirme dieselben Felder brauchen:
 * die Stammdaten am Teil (D-40) und die Kategorie-Startwerte (D-41).
 */
export const EINGABE =
  'mt-1 w-full rounded-[var(--radius-control)] bg-white/6 px-3.5 py-3 text-[15px] ring-1 ring-white/10 outline-none focus:ring-2 focus:ring-[var(--color-accent)]';

export function Feld({ label, hinweis, children }: {
  label: string;
  hinweis?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="mt-3 block">
      <span className="block text-xs text-white/55">{label}</span>
      {children}
      {hinweis !== undefined && (
        <span className="mt-1 block text-[11px] text-white/30">{hinweis}</span>
      )}
    </label>
  );
}

export function Abschnitt({ titel }: { titel: string }): ReactNode {
  return (
    <p className="mt-5 border-t border-white/8 pt-4 text-[12px] font-medium text-white/40">
      {titel}
    </p>
  );
}

/**
 * Mehrfachauswahl über Teile, mit Suche. Lag in `ItemDetail` für die
 * Kombinierbarkeit (D-31) und wird seit „Ich trage etwas anderes" (D-36) an zwei Stellen
 * gebraucht — dieselbe Liste, dieselbe Suche, derselbe Umgang mit einer leeren Auswahl.
 */
export function ItemPicker({
  titel, hinweis, kandidaten, vorausgewaehlt, busy, onAbbrechen, onSpeichern,
  speichernLabel = (n) => (n === 0 ? 'Ohne Einschränkung speichern' : `${n} Teile speichern`),
}: {
  titel: string;
  hinweis: string;
  kandidaten: { id: string; name: string; category: string }[];
  vorausgewaehlt: Set<string>;
  busy: boolean;
  onAbbrechen: () => void;
  onSpeichern: (ids: string[]) => void;
  speichernLabel?: (anzahl: number) => string;
}): ReactNode {
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set(vorausgewaehlt));
  const [suche, setSuche] = useState('');
  const gefiltert = kandidaten.filter((k) =>
    `${k.name} ${categoryLabel(k.category)}`.toLowerCase().includes(suche.toLowerCase()),
  );

  return (
    <Card>
      <p className="text-[15px] font-semibold">{titel}</p>
      <p className="mt-1 text-[13px] leading-relaxed text-white/45">{hinweis}</p>

      <input
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
        placeholder="Suchen …"
        className="mt-3 w-full rounded-[var(--radius-control)] bg-white/6 px-3.5 py-3 text-[15px] ring-1 ring-white/10 outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
      />

      <div className="mt-3 max-h-72 space-y-1 overflow-y-auto">
        {gefiltert.map((k) => {
          const an = gewaehlt.has(k.id);
          return (
            <button
              key={k.id}
              onClick={() => {
                const next = new Set(gewaehlt);
                if (an) next.delete(k.id);
                else next.add(k.id);
                setGewaehlt(next);
              }}
              className={`press flex w-full items-center justify-between rounded-[var(--radius-control)] px-3 py-2.5 text-left text-[14px] ${
                an ? 'bg-[var(--color-accent-soft)] text-white' : 'bg-white/6 text-white/70'
              }`}
            >
              <span className="truncate">
                {k.name}
                <span className="ml-2 text-[11px] text-white/35">{categoryLabel(k.category)}</span>
              </span>
              {an && <span className="ml-2 shrink-0 text-[var(--color-accent)]">✓</span>}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex gap-2">
        <Button className="flex-1" disabled={busy} onClick={() => onSpeichern([...gewaehlt])}>
          {speichernLabel(gewaehlt.size)}
        </Button>
        <Button variant="ghost" onClick={onAbbrechen}>
          Abbrechen
        </Button>
      </div>
    </Card>
  );
}

/**
 * Ein Schalter mit Beschriftung und Erklärung. Lag als lokale Komponente in „Mehr" und
 * wird seit dem Vorschlags-Schalter am Teil an zwei Stellen gebraucht — zwei Kopien
 * desselben Schalters wären zwei Gelegenheiten, dass sie sich unterscheiden.
 */
export function Toggle({ label, hint, value, onChange, disabled = false }: {
  label: string;
  hint: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}): ReactNode {
  return (
    <div className="mt-4 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-white/85">{label}</p>
        <p className="mt-0.5 text-[11px] leading-relaxed text-white/30">{hint}</p>
      </div>
      <button
        onClick={() => onChange(!value)}
        disabled={disabled}
        className={`h-8 w-14 shrink-0 rounded-full p-1 transition ${value ? 'bg-sky-500' : 'bg-slate-700'} ${disabled ? 'opacity-50' : ''}`}
        role="switch"
        aria-checked={value}
        aria-label={label}
      >
        <span
          className={`block size-6 rounded-full bg-white transition ${value ? 'translate-x-6' : ''}`}
        />
      </button>
    </div>
  );
}

export function StateBadge({ state }: { state: string }): ReactNode {
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
        STATE_COLOR[state] ?? 'bg-white/8 text-white/60'
      }`}
    >
      {labelOf.lifecycleState(state)}
    </span>
  );
}

/*
 * Hier stand eine Liste der Anlaesse aus dem alten Enum. Seit D-34 stehen sie in der
 * Datenbank und werden ueber /api/occasions geladen — die Konstante war eine zweite
 * Wahrheit, die niemand mehr las.
 */

/**
 * Beschriftungen und Zahlformate kommen aus packages/shared — eine Quelle fuer Server
 * und Frontend. Diese Kurznamen bleiben, damit die Bildschirme knapp lesbar sind.
 */
export const categoryLabel = labelOf.category;
export const slotLabel = labelOf.slot;
export const layerLabel = labelOf.layer;
export const carePolicyLabel = labelOf.carePolicy;
export const laundryGroupLabel = labelOf.laundryGroup;
export const itemActionLabel = labelOf.itemAction;
export const tripStatusLabel = labelOf.tripStatus;
export const learnedFieldLabel = labelOf.learnedField;
export const learnedValue = formatLearnedValue;
export const num = formatNumber;
export { formatDate } from '@wardrobe/shared';

/** Feste Nachkommastellen — in der Bewertungsaufschluesselung sollen Zahlen buendig stehen. */
export function numFixed(value: number, stellen = 2): string {
  return value.toLocaleString('de-DE', {
    minimumFractionDigits: stellen,
    maximumFractionDigits: stellen,
  });
}

export function formatCents(cents: number | null): string {
  if (cents === null) return '—';
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}
